import React, { createContext, useEffect } from 'react';
import {
  useAccount,
  useConnect,
  usePublicClient,
  useWalletClient,
  useDisconnect,
  useSwitchChain,
} from 'wagmi';
import { useStateIfMounted } from 'use-state-if-mounted';

import { DEFAULT_NETWORK } from 'constants/index';

export const WalletContext = createContext(null);

export const WalletProvider = ({ children }) => {
  const { address: rawAddress, isConnected, chainId } = useAccount();
  const { connect, connectors: rawConnectors, connectAsync } = useConnect();
  const { disconnect } = useDisconnect();
  const { data: signer } = useWalletClient({ chainId });
  const provider = usePublicClient();
  const { switchChainAsync } = useSwitchChain();

  const [walletModalOpen, setWalletModalOpen] = useStateIfMounted(false);

  // Check URL for chain parameter
  const getInitialChain = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const chainFromUrl = urlParams.get('chain') || urlParams.get('chainId');
    if (chainFromUrl) {
      return Number(chainFromUrl);
    }
    return Number(localStorage.getItem('storedChain')) || DEFAULT_NETWORK;
  };

  const [storedChain, setStoredChain] = useStateIfMounted(getInitialChain());
  const [wrongNetwork, setWrongNetwork] = useStateIfMounted(false);

  const address = rawAddress?.toLowerCase();
  const isInstalled = typeof window.ethereum !== 'undefined';
  const connectors = rawConnectors
    .slice()
    .sort((prev, next) => (prev.name === 'MetaMask' ? -1 : next.name === 'MetaMask' ? 1 : 0));

  const globalChain = isConnected ? chainId : storedChain;

  const updateStoredChain = (targetChain) => {
    localStorage.setItem('storedChain', targetChain);
    setStoredChain(Number(targetChain));
  };

  const switchToNetwork = async (chainId) => {
    return await switchChainAsync({ chainId });
  };

  useEffect(() => {
    const manuallyDisconnected = sessionStorage.getItem('manuallyDisconnected');
    if (manuallyDisconnected === 'true' && isConnected) {
      disconnect();
    }
  }, [isConnected, disconnect]);

  useEffect(() => {
    if (!localStorage.getItem('storedChain')) {
      localStorage.setItem('storedChain', DEFAULT_NETWORK);
    }

    if (localStorage.getItem('manuallyDisconnected') === 'true') {
      localStorage.removeItem('manuallyDisconnected');
    }
  }, []);

  const enhancedDisconnect = () => {
    sessionStorage.setItem('manuallyDisconnected', 'true');
    disconnect();
  };

  const enhancedConnect = (connectorOrOptions) => {
    sessionStorage.removeItem('manuallyDisconnected');
    return connect(connectorOrOptions);
  };

  const enhancedConnectAsync = async (connectorOrOptions) => {
    sessionStorage.removeItem('manuallyDisconnected');
    const result = await connectAsync(connectorOrOptions);

    // After successful connection, if wallet is on wrong chain, try to switch
    if (result && result.accounts && result.accounts.length > 0) {
      const currentAppChain = storedChain;
      if (result.chainId !== currentAppChain) {
        try {
          await switchToNetwork(currentAppChain);
        } catch (err) {
          console.warn('Failed to switch to app chain after connection:', err);
          // User rejected the switch or chain not added to wallet
          // The app will still work but show content for the wallet's chain
        }
      }
    }

    return result;
  };

  // Handle chain from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chainFromUrl = urlParams.get('chain') || urlParams.get('chainId');

    if (chainFromUrl) {
      const targetChain = Number(chainFromUrl);

      // Update stored chain to match URL parameter
      if (storedChain !== targetChain) {
        updateStoredChain(targetChain);
      }

      // If connected and on wrong chain, try to switch
      if (isConnected && chainId !== targetChain) {
        switchToNetwork(targetChain).catch((err) => {
          console.error('Failed to switch network:', err);
        });
      }

      // Clean up URL after processing
      urlParams.delete('chain');
      const newUrl =
        window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  return (
    <WalletContext.Provider
      value={{
        isInstalled,
        isConnected,
        connectors,
        connect: enhancedConnect,
        connectAsync: enhancedConnectAsync,
        provider,
        signer,
        chainId,
        address,
        disconnect: enhancedDisconnect,
        switchToNetwork,
        walletModalOpen,
        setWalletModalOpen,
        wrongNetwork,
        setWrongNetwork,
        storedChain,
        globalChain,
        updateStoredChain,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
